import { coreApi } from '$api'
import {
  ClockinResponse,
  IClockinHistory,
  IClockinResponse,
  IShiftResponse,
  transformClockinHistoryOnGet,
} from '$models'
import { singletonQueryClient } from '$utils'
import { Dayjs } from 'dayjs'

enum QueryKeys {
  getClockins = 'clockins',
  getClockinHistories = 'clockin-histories',
}

/**
 * Get all Clockins
 * @param startDateTime - include all the clockins from a specific time
 * @param endDateTime - include all the clockins until a specific time
 * @param includeInactive - include the inactive ones
 * @param includeOnlyClockins - include only shifts of type Clockin
 * @param staffMemberIds - the ids of the staff members to get the clocks for
 * @param shouldTruncateShifts - Denotes if we should truncate the shifts or not
 * @returns Promise<IStaffMemberDetail[]>
 */
export const getClockins = async (
  startDateTime: Dayjs,
  endDateTime: Dayjs,
  includeInactive: boolean = false,
  includeOnlyClockins: boolean = false,
  staffMemberIds?: string[],
  shouldTruncateShifts: boolean = true
): Promise<IShiftResponse[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getClockins,
      startDateTime,
      endDateTime,
      includeInactive,
      includeOnlyClockins,
      staffMemberIds,
      shouldTruncateShifts,
    ],
    queryFn: async () => {
      const clockins = await coreApi.getClockins(
        startDateTime,
        endDateTime,
        includeInactive,
        undefined,
        includeOnlyClockins,
        staffMemberIds,
        shouldTruncateShifts
      )

      return clockins
    },
  })
}

/**
 * Update clockin detail
 * @param clockinDetail - clockinDetail to update
 * @returns Promise<ClockinDetail>
 */
export const update = async (
  clockinDetail: IClockinResponse
): Promise<IClockinResponse> => {
  const updatedClockinDetail = await coreApi.updateClockin(
    clockinDetail.id!,
    clockinDetail as ClockinResponse
  )

  return updatedClockinDetail
}

export const add = async (
  clockinDetail: IClockinResponse
): Promise<IClockinResponse> => {
  const newClockinDetail = await coreApi.addClockin(
    clockinDetail as ClockinResponse
  )

  return newClockinDetail
}

export const getClockinHistory = async (
  operation: string | undefined,
  startTime: Dayjs | undefined,
  endTime: Dayjs | undefined,
  clockinId: string | undefined,
  page: number,
  pageSize: number,
  sort: string | undefined
): Promise<IClockinHistory[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getClockinHistories,
      clockinId,
      operation,
      startTime,
      endTime,
      page,
      pageSize,
      sort,
    ],
    queryFn: async () => {
      const clockinHistories = await coreApi.getClockinHistories(
        operation,
        startTime,
        endTime,
        clockinId,
        page,
        pageSize,
        sort
      )

      return clockinHistories.result.map(transformClockinHistoryOnGet)
    },
  })
}
